import pino from 'pino'
import * as Sentry from '@sentry/node'
import { createRequire } from 'node:module'
import { createWriteStream, mkdirSync } from 'node:fs'
import { dirname } from 'node:path'
import type { LoggerConfig, LoggerService } from './types.js'

const isDev = process.env['NODE_ENV'] !== 'production'

/**
 * Detects whether pino-pretty is available.
 * In production or when it is not installed, returns false.
 */
function hasPinoPretty(): boolean {
  if (!isDev) return false
  try {
    const require = createRequire(import.meta.url)
    require.resolve('pino-pretty')
    return true
  } catch {
    return false
  }
}

let loggerInstance: LoggerService | null = null
let pinoInstance: pino.Logger | null = null
let sentryEnabled = false

/**
 * GDPR-compliant redaction paths for sensitive data.
 * These fields will be replaced with [REDACTED] in logs.
 */
const REDACT_PATHS = [
  // Auth credentials
  'password',
  'currentPassword',
  'newPassword',
  'token',
  'accessToken',
  'refreshToken',
  'jwt',
  'apiKey',
  'secret',
  'authorization',
  // Nested in request body
  'req.body.password',
  'req.body.currentPassword',
  'req.body.newPassword',
  'req.body.token',
  'req.headers.authorization',
  // Cookies (may contain session tokens)
  'req.headers.cookie',
  // Error context
  'err.config.headers.authorization',
  'error.config.headers.authorization'
]


/**
 * Initializes the logger service with Pino and optional Sentry
 */
export function initLoggerService(config: LoggerConfig): LoggerService {
  // Si ya está inicializado, retornar instancia existente (idempotente)
  if (loggerInstance) {
    return loggerInstance
  }

  // Inicializar Pino with GDPR-compliant redaction
  const usePretty = config.format === 'pretty' && hasPinoPretty()
  const pinoOpts: pino.LoggerOptions = {
    level: config.level,
    redact: {
      paths: REDACT_PATHS,
      censor: '[REDACTED]'
    },
  }

  // Resolve pino-pretty as a stream (not transport worker) — reliable with pnpm.
  let prettyStdout: NodeJS.WritableStream | null = null
  if (usePretty) {
    try {
      const require = createRequire(import.meta.url)
      const pinoPretty = require('pino-pretty') as (opts: object) => NodeJS.WritableStream
      prettyStdout = pinoPretty({ colorize: true, sync: true })
    } catch {
      // Fallback: raw JSON to stdout
    }
  }

  let pinoLogger: pino.Logger
  if (config.logFile) {
    mkdirSync(dirname(config.logFile), { recursive: true })
    const fileStream = createWriteStream(config.logFile, { flags: 'a' })
    const streams: pino.StreamEntry[] = [
      { stream: prettyStdout ?? process.stdout },
      { stream: fileStream },
    ]
    pinoLogger = pino(pinoOpts, pino.multistream(streams))
  } else if (prettyStdout) {
    pinoLogger = pino(pinoOpts, prettyStdout)
  } else {
    pinoLogger = pino(pinoOpts)
  }

  // Inicializar Sentry solo si DSN está configurado
  sentryEnabled = !!config.sentry
  if (sentryEnabled && config.sentry) {
    Sentry.init({
      dsn: config.sentry.dsn,
      environment: config.sentry.environment,
      sampleRate: config.sentry.sampleRate
    })
    pinoLogger.info({ sentry: config.sentry.dsn }, 'Sentry initialized')
  }

  // Guardar instancia de pino para shared/logger.ts
  pinoInstance = pinoLogger

  loggerInstance = {
    fatal: pinoLogger.fatal.bind(pinoLogger),
    error: pinoLogger.error.bind(pinoLogger),
    warn: pinoLogger.warn.bind(pinoLogger),
    info: pinoLogger.info.bind(pinoLogger),
    debug: pinoLogger.debug.bind(pinoLogger),
    trace: pinoLogger.trace.bind(pinoLogger),
    child: pinoLogger.child.bind(pinoLogger),

    // Sentry helpers (no-op si no está habilitado)
    captureException: (error: Error, context?: Record<string, unknown>) => {
      if (sentryEnabled) {
        Sentry.captureException(error, { extra: context })
      }
    },
    captureMessage: (msg: string, level: 'info' | 'warning' | 'error' = 'info') => {
      if (sentryEnabled) {
        Sentry.captureMessage(msg, level)
      }
    },
    setUser: (user: { id: string; email?: string }) => {
      if (sentryEnabled) {
        Sentry.setUser(user)
      }
    },
    isSentryEnabled: () => sentryEnabled
  }

  return loggerInstance
}

/**
 * Gets the LoggerService instance (singleton)
 */
export function getLoggerService(): LoggerService {
  if (!loggerInstance) {
    throw new Error('Logger not initialized. Call initLoggerService() first.')
  }
  return loggerInstance
}

/**
 * Resets the logger service (tests only)
 */
export function resetLoggerService(): void {
  loggerInstance = null
  pinoInstance = null
  sentryEnabled = false
}

/**
 * Gets the pino.Logger instance (for ctx.logger)
 */
export function getPinoLogger(): pino.Logger {
  if (!pinoInstance) {
    throw new Error('Logger not initialized. Call initLoggerService() first.')
  }
  return pinoInstance
}

export function captureExceptionSafe(error: Error, context?: Record<string, unknown>): void {
  if (loggerInstance?.isSentryEnabled()) {
    loggerInstance.captureException(error, context)
  }
}
